import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button';
import { RHFRadioField } from '@openx/components-core/lib/RadioField/RHFRadioField';
import type { DspRestriction } from '@openx/types/gqlTypes';
import { dspRestrictionModes } from '@openx/types/organization';
import { useCallback, useEffect } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { organizationFields } from '../../../constants';
import type { OrganizationFormData } from '../../../types';
import { useOrganizationContext } from '../../../utils/context/OrganizationContext';
import { DspRestrictionTooltip } from '../../DspRestrictionTooltip';
import { DspRestrictionFormInputs } from './DspRestrictionFormInputs';

type DspRestrictionExtendedSectionProps = {
  isEdit?: boolean;
  initialFormValues?: OrganizationFormData;
};

// Conversion to unknown is intentional due placholder having null for dso_id
const DSP_PLACEHOLDER_ITEM = { dsp_id: null, seat_ids: [] } as unknown as DspRestriction;

export const DspRestrictionExtendedSection = ({ isEdit, initialFormValues }: DspRestrictionExtendedSectionProps) => {
  const { t } = useTranslation();

  const { control, getValues } = useFormContext<OrganizationFormData>();

  const observableDspRestrictionMode = useWatch<Pick<OrganizationFormData, 'dsp_restriction_mode'>>({
    name: organizationFields.DSP_RESTRICTION_MODE,
  });

  const { fields, append, remove } = useFieldArray<Pick<OrganizationFormData, 'dsp_restriction'>>({
    name: organizationFields.DSP_RESTRICTION,
  });

  const { useDspsFetch } = useOrganizationContext();
  const { mapById: dspOptions, loading: dspLoading } = useDspsFetch();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only on init
  useEffect(() => {
    if (isEdit && (!initialFormValues?.dsp_restriction || initialFormValues.dsp_restriction.length === 0)) {
      return;
    }
    if (fields.length === 0) {
      append(DSP_PLACEHOLDER_ITEM);
    }
  }, []);

  const handleRemoveDspRestriction = useCallback(
    (index: number) => {
      remove(index);

      const newFieldsLength = fields.length - 1;
      if (newFieldsLength === 0) {
        append(DSP_PLACEHOLDER_ITEM);
      }
    },
    [remove, fields.length, append]
  );

  const addAnotherDsp = useCallback(() => {
    append(DSP_PLACEHOLDER_ITEM);
  }, [append]);

  const handleRestrictionModeChange = useCallback(() => {
    // Clear all existing fields and add a single empty one
    for (let i = fields.length - 1; i >= 0; i--) {
      remove(i);
    }
    append(DSP_PLACEHOLDER_ITEM);
  }, [fields.length, remove, append]);

  const restrictionModeOptions = [
    { label: t('Allow List'), value: dspRestrictionModes.ALLOW, 'data-test': 'dsp-restriction-mode-allow' },
    { label: t('Block List'), value: dspRestrictionModes.BLOCK, 'data-test': 'dsp-restriction-mode-block' },
  ];

  const tooltipInfoTextLines = [
    t('Configure an Allow or Block list for DSPs and seats used by a Buyer or Tech Partner organization.'),
    t(
      'Allowed DSPs and seats cannot be removed after saving due to potential use in live deals.Blocked DSPs and seats can be removed at any time.'
    ),
  ];

  const isDspRestricitonModesDisabled =
    isEdit &&
    Array.isArray(initialFormValues?.dsp_restriction) &&
    initialFormValues.dsp_restriction.length > 0 &&
    initialFormValues.dsp_restriction_mode === dspRestrictionModes.ALLOW;

  return (
    <Box
      sx={{
        mb: 0.5,
        mt: 2,
        borderLeft: '3px solid',
        borderColor: 'divider',
        pl: 1.5,
      }}
    >
      <Grid size={12}>
        <Typography variant="body1Bold" data-test="dsp-seats-label" sx={{ textTransform: 'uppercase' }}>
          {t('DSP Configuration')}
          <DspRestrictionTooltip tooltipTextLines={tooltipInfoTextLines} />
        </Typography>
      </Grid>
      <Grid
        size={12}
        sx={{
          mb: 2,
        }}
      >
        <RHFRadioField
          name={organizationFields.DSP_RESTRICTION_MODE}
          options={restrictionModeOptions}
          data-test="dsp-restriction-mode"
          isDisabled={isDspRestricitonModesDisabled}
          row
          sx={{ gap: 1 }}
          onChange={handleRestrictionModeChange}
        />
      </Grid>
      {observableDspRestrictionMode && (
        <Grid
          container
          wrap="nowrap"
          sx={{
            alignItems: 'flex-start',
          }}
        >
          <Grid size={{ xs: 12 }} sx={{ minWidth: '100%' }}>
            {fields.map((field, index) => {
              const isInitial =
                Array.isArray(initialFormValues?.dsp_restriction) && index < initialFormValues.dsp_restriction.length;
              const initialDspId = isInitial ? initialFormValues.dsp_restriction?.[index].dsp_id : null;
              const initialSeatIds = isInitial ? initialFormValues.dsp_restriction?.[index].seat_ids : [];

              // Get all selected DSP IDs except the current one
              const selectedDspIds = new Set(
                (getValues(organizationFields.DSP_RESTRICTION) ?? [])
                  .map((item, idx) => (idx === index ? null : item?.dsp_id))
                  .filter((id): id is string => id !== null && id !== undefined)
              );

              // Filter dspOptions to exclude already selected DSPs
              const filteredDspOptions = Object.fromEntries(
                Object.entries(dspOptions).filter(([key]) => !selectedDspIds.has(key))
              );

              return (
                <DspRestrictionFormInputs
                  key={field.id}
                  index={index}
                  fieldId={field.id}
                  control={control}
                  dspOptions={filteredDspOptions}
                  dspLoading={dspLoading}
                  remove={handleRemoveDspRestriction}
                  canRemove={true}
                  isInitial={isInitial}
                  initialDspId={initialDspId}
                  initialDspRestrictionMode={initialFormValues?.dsp_restriction_mode}
                  initialSeatIds={initialSeatIds}
                  showSeatIdOnlyWhenDspSelected={true}
                  noSeatsInfoMessage={t('Seats are not configured for this DSP.')}
                  enableSelectDeselectAll={true}
                />
              );
            })}
            <Box>
              <Button onClick={addAnotherDsp} data-test="add-dsp" color="primary" type="button" variant="text">
                {t('Add another DSP')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
